import { useEffect, useState } from 'react';
import {
  Container, Typography, Box, Paper, Alert, CircularProgress, Chip, Stack,
} from '@mui/material';
import Chart from 'react-apexcharts';
import { getPlatformOverview } from '../../api/saasApi';
import { brand } from '../../theme/tokens';

const Tile = ({ label, value, hint, tone }) => (
  <Paper sx={{ p: 2.5, height: '100%' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="h4" fontWeight={700} color={tone || 'text.primary'} sx={{ my: 0.5 }}>
      {value ?? 0}
    </Typography>
    {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
  </Paper>
);

/** Product-wide health for platform admins: who's on it and what they're doing. */
const Overview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await getPlatformOverview();
        if (res.data?.succeeded === false) setError(res.data.messages?.[0] || 'Failed to load overview.');
        else setData(res.data?.data || null);
      } catch (e) {
        setError('Failed to load overview.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error) return <Container sx={{ py: 4 }}><Alert severity="warning">{error}</Alert></Container>;

  const points = data?.signupsLast30Days || [];

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={2}>Platform overview</Typography>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mb: 2,
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        }}
      >
        <Tile label="Clinics" value={data?.tenantsTotal} hint="all time" />
        <Tile label="Users" value={data?.usersTotal} hint={`${data?.usersActive ?? 0} approved`} />
        <Tile
          label="Awaiting approval"
          value={data?.usersPending}
          tone={data?.usersPending > 0 ? 'warning.main' : undefined}
          hint="across all clinics"
        />
        <Tile label="Assessments" value={data?.assessmentsSubmitted} hint="submissions" />
      </Box>

      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Typography variant="body2" color="text.secondary" mb={1}>Clinics by status</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={`Trialing: ${data?.tenantsTrialing ?? 0}`} color="info" />
          <Chip label={`Active: ${data?.tenantsActive ?? 0}`} color="success" />
          <Chip label={`Lapsed: ${data?.tenantsLapsed ?? 0}`} color={data?.tenantsLapsed > 0 ? 'error' : 'default'} />
          <Chip label={`Patients: ${data?.patientsTotal ?? 0}`} variant="outlined" />
        </Stack>
      </Paper>

      <Paper sx={{ p: 2.5 }}>
        <Typography variant="body2" color="text.secondary" mb={1}>Signups, last 30 days</Typography>
        {points.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No signups in the last 30 days.</Typography>
        ) : (
          <Chart
            type="area"
            height={260}
            series={[{ name: 'Signups', data: points.map((p) => p.count) }]}
            options={{
              chart: { toolbar: { show: false }, zoom: { enabled: false } },
              dataLabels: { enabled: false },
              stroke: { curve: 'smooth', width: 2 },
              colors: [brand[500]],
              fill: { type: 'gradient', gradient: { shadeIntensity: 0.4, opacityFrom: 0.4, opacityTo: 0.05 } },
              xaxis: {
                categories: points.map((p) => new Date(p.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })),
                tickAmount: 8,
              },
              yaxis: { labels: { formatter: (v) => Math.round(v) } },
              grid: { borderColor: '#eee' },
            }}
          />
        )}
      </Paper>
    </Container>
  );
};

export default Overview;
